import { useState, useEffect } from "react";
import axios from "axios";
import { 
  Sparkles, Search, CheckCircle2, XCircle, 
  TrendingUp, RefreshCw, Play, Trash2, Check,
  AlertTriangle, FileText, Target
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
});

export default function AdminSEO() {
  const [analyses, setAnalyses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [analyzeAllLoading, setAnalyzeAllLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [analysesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/seo/analyses`, { headers: getAuthHeaders() }),
        axios.get(`${API}/categories`),
      ]);
      setAnalyses(analysesRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnalyze = async (pageType, pageId = null) => {
    setAnalyzing(true);
    try {
      const url = pageId 
        ? `${API}/seo/analyze?page_type=${pageType}&page_id=${pageId}`
        : `${API}/seo/analyze?page_type=${pageType}`;
      
      const response = await axios.post(url, {}, { headers: getAuthHeaders() });
      
      toast.success("SEO analizi tamamlandı", {
        description: `Skor: ${response.data.score}/100`
      });
      
      fetchData();
    } catch (error) {
      toast.error("Analiz başarısız", {
        description: error.response?.data?.detail || "Bir hata oluştu"
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleAnalyzeAll = async () => {
    setAnalyzeAllLoading(true);
    try {
      const response = await axios.post(`${API}/seo/analyze-all`, {}, { headers: getAuthHeaders() });
      toast.success("Tüm kategoriler analiz edildi", {
        description: response.data.message
      });
      fetchData();
    } catch (error) {
      toast.error("Toplu analiz başarısız");
    } finally {
      setAnalyzeAllLoading(false);
    }
  };

  const handleApply = async (analysisId) => {
    try {
      await axios.post(`${API}/seo/apply/${analysisId}`, {}, { headers: getAuthHeaders() });
      toast.success("SEO önerileri uygulandı");
      fetchData();
    } catch (error) {
      toast.error("Öneriler uygulanamadı");
    }
  };

  const handleDelete = async (analysisId) => {
    if (!window.confirm("Bu analizi silmek istediğinize emin misiniz?")) return;
    try {
      await axios.delete(`${API}/seo/analyses/${analysisId}`, { headers: getAuthHeaders() });
      toast.success("Analiz silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "text-signal-500";
    if (score >= 60) return "text-yellow-500";
    if (score >= 40) return "text-orange-500";
    return "text-red-500";
  };

  const getScoreBadge = (score) => {
    if (score >= 80) return "bg-signal-100 text-signal-700";
    if (score >= 60) return "bg-yellow-100 text-yellow-700";
    if (score >= 40) return "bg-orange-100 text-orange-700";
    return "bg-red-100 text-red-700";
  };

  const averageScore = analyses.length > 0 
    ? Math.round(analyses.reduce((sum, a) => sum + (a.score || 0), 0) / analyses.length)
    : 0;

  return (
    <AdminLayout title="AI SEO Optimizasyonu">
      <div className="space-y-6" data-testid="admin-seo">
        {/* Header */}
        <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
          <div>
            <p className="text-slate-600">
              OpenAI destekli SEO analizi ile sayfalarınızı optimize edin.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleAnalyzeAll}
              disabled={analyzeAllLoading}
              className="bg-purple-500 hover:bg-purple-600"
            >
              {analyzeAllLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Analiz Ediliyor...
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  Tüm Kategorileri Analiz Et
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Toplam Analiz</p>
                  <p className="text-3xl font-bold text-navy-900">{analyses.length}</p>
                </div>
                <div className="w-12 h-12 bg-electric-100 rounded-xl flex items-center justify-center">
                  <FileText className="w-6 h-6 text-electric-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Ortalama SEO Skoru</p>
                  <p className={`text-3xl font-bold ${getScoreColor(averageScore)}`}>
                    {averageScore}/100
                  </p>
                </div>
                <div className="w-12 h-12 bg-signal-100 rounded-xl flex items-center justify-center">
                  <Target className="w-6 h-6 text-signal-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Uygulanan Öneriler</p>
                  <p className="text-3xl font-bold text-navy-900">
                    {analyses.filter(a => a.status === 'applied').length}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* New Analysis */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Yeni SEO Analizi Başlat
            </CardTitle>
            <CardDescription>
              Analiz etmek istediğiniz sayfa türünü ve kategoriyi seçin
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full sm:w-[300px]">
                  <SelectValue placeholder="Kategori seçin" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="homepage">Ana Sayfa</SelectItem>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                onClick={() => {
                  if (selectedCategory === 'homepage') {
                    handleAnalyze('homepage');
                  } else if (selectedCategory) {
                    handleAnalyze('category', selectedCategory);
                  } else {
                    toast.error("Lütfen bir kategori seçin");
                  }
                }}
                disabled={analyzing || !selectedCategory}
                className="bg-electric-500 hover:bg-electric-600"
              >
                {analyzing ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Analiz Ediliyor...
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    AI ile Analiz Et
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Analyses List */}
        <Card className="border-0 shadow-sm">
          <CardHeader>
            <CardTitle>SEO Analiz Sonuçları</CardTitle>
            <CardDescription>
              Tüm SEO analizleri ve öneriler
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse"></div>
                ))}
              </div>
            ) : analyses.length > 0 ? (
              <Accordion type="single" collapsible className="space-y-3">
                {analyses.map((analysis, index) => (
                  <AccordionItem 
                    key={analysis.id} 
                    value={`analysis-${index}`}
                    className="bg-slate-50 border border-slate-200 rounded-xl px-4"
                  >
                    <AccordionTrigger className="hover:no-underline py-4">
                      <div className="flex items-center justify-between w-full pr-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${getScoreBadge(analysis.score)}`}>
                            <span className="font-bold">{analysis.score}</span>
                          </div>
                          <div className="text-left">
                            <p className="font-medium text-navy-900">
                              {analysis.page_type === 'homepage' ? 'Ana Sayfa' : 
                               analysis.page_type === 'category' ? 'Kategori' : analysis.page_type}
                            </p>
                            <p className="text-sm text-slate-500">
                              {analysis.current_title?.substring(0, 50)}...
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={analysis.status === 'applied' ? 'bg-signal-100 text-signal-700' : 'bg-slate-100 text-slate-600'}>
                            {analysis.status === 'applied' ? 'Uygulandı' : 'Bekliyor'}
                          </Badge>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pb-4">
                      <div className="space-y-4 pt-2">
                        {/* Score Progress */}
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span>SEO Skoru</span>
                            <span className={`font-medium ${getScoreColor(analysis.score)}`}>
                              {analysis.score}/100
                            </span>
                          </div>
                          <Progress value={analysis.score} className="h-2" />
                        </div>

                        {/* Current vs Suggested */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <h4 className="font-medium text-navy-900">Mevcut Başlık</h4>
                            <p className="text-sm text-slate-600 bg-white p-3 rounded-lg border">
                              {analysis.current_title || '-'}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <h4 className="font-medium text-signal-600 flex items-center gap-1">
                              <Sparkles className="w-4 h-4" />
                              Önerilen Başlık
                            </h4>
                            <p className="text-sm text-slate-600 bg-signal-50 p-3 rounded-lg border border-signal-200">
                              {analysis.suggested_title || '-'}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <h4 className="font-medium text-navy-900">Mevcut Açıklama</h4>
                            <p className="text-sm text-slate-600 bg-white p-3 rounded-lg border">
                              {analysis.current_description || '-'}
                            </p>
                          </div>
                          <div className="space-y-2">
                            <h4 className="font-medium text-signal-600 flex items-center gap-1">
                              <Sparkles className="w-4 h-4" />
                              Önerilen Açıklama
                            </h4>
                            <p className="text-sm text-slate-600 bg-signal-50 p-3 rounded-lg border border-signal-200">
                              {analysis.suggested_description || '-'}
                            </p>
                          </div>
                        </div>

                        {/* Keywords */}
                        {analysis.suggested_keywords?.length > 0 && (
                          <div className="space-y-2">
                            <h4 className="font-medium text-navy-900">Önerilen Anahtar Kelimeler</h4>
                            <div className="flex flex-wrap gap-2">
                              {analysis.suggested_keywords.map((keyword, i) => (
                                <Badge key={i} variant="outline" className="bg-white">
                                  {keyword}
                                </Badge>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Content Suggestions */}
                        {analysis.content_suggestions?.length > 0 && (
                          <div className="space-y-2">
                            <h4 className="font-medium text-navy-900">İçerik Önerileri</h4>
                            <ul className="space-y-2">
                              {analysis.content_suggestions.map((suggestion, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                  <TrendingUp className="w-4 h-4 text-signal-500 mt-0.5 flex-shrink-0" />
                                  {suggestion}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Issues */}
                        {analysis.issues?.length > 0 && (
                          <div className="space-y-2">
                            <h4 className="font-medium text-red-600 flex items-center gap-1">
                              <AlertTriangle className="w-4 h-4" />
                              Tespit Edilen Sorunlar
                            </h4>
                            <ul className="space-y-2">
                              {analysis.issues.map((issue, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                  <XCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                                  {issue}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-2 pt-2 border-t">
                          {analysis.status !== 'applied' && (
                            <Button
                              onClick={() => handleApply(analysis.id)}
                              className="bg-signal-500 hover:bg-signal-600"
                            >
                              <Check className="w-4 h-4 mr-2" />
                              Önerileri Uygula
                            </Button>
                          )}
                          <Button
                            variant="outline"
                            onClick={() => handleDelete(analysis.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Sil
                          </Button>
                        </div>

                        {/* Timestamp */}
                        <p className="text-xs text-slate-400">
                          Analiz tarihi: {new Date(analysis.created_at).toLocaleString('tr-TR')}
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <Sparkles className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Henüz SEO analizi yapılmamış.</p>
                <p className="text-sm">Yukarıdan bir kategori seçerek analiz başlatın.</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
